import { CircleSlash, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HttpActionExecutionMode } from '@/Domain/api/Action/dto/Action.dto';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { formatRelativeTime } from '@/Domain/helper/RelativeTime.helper';
import { formatMissingValues, getSkipReasonText } from '@/Domain/helper/actionHealth.helper';
import { ActionHealth } from '@/Domain/hooks/Action/useActionHealth.hook';
import { useActionSkips } from '@/Domain/hooks/Action/useActionSkips.hook';
import { SCENARIO_ID } from '@/context';

type Props = {
  actionId: number;
  health: ActionHealth;
};

export const ActionSkipSummary = ({ actionId, health }: Props) => {
  const { t } = useTranslation('actions_page');
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useActionSkips(actionId);

  const skips = data?.pages.flatMap((page) => page.skips) ?? [];
  const reason = getSkipReasonText(health);
  const showAdvice = health.skip_reason === 'missing_values' && reason !== null;
  const modeAdvice: Record<HttpActionExecutionMode, string> = {
    ai_decides: t('skips.advice.ai_decides'),
    when_metadata_matches: t('skips.advice.when_metadata_matches'),
    before_first_reply: t('skips.advice.before_first_reply'),
  };

  return (
    <div className="space-y-3">
      <Alert variant="warning" data-testid="action-skip-summary">
        <CircleSlash className="size-4" />
        <AlertTitle className="leading-snug">
          {t('health.not_running_reason', {
            count: health.skipped_conversations,
            days: health.window_days,
          })}
        </AlertTitle>
        <AlertDescription className="space-y-2">
          {reason && <p data-testid="action-skip-reason">{reason}</p>}
          {showAdvice && (
            <div className="space-y-1" data-testid="action-skip-advice">
              <p>{t('skips.advice.conversation_values')}</p>
              {health.execution_mode && <p>{modeAdvice[health.execution_mode]}</p>}
            </div>
          )}
        </AlertDescription>
      </Alert>

      {(isLoading || skips.length > 0) && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">{t('skips.recent_title')}</h3>
          {isLoading ? (
            <div className="flex justify-center py-4" data-testid="action-skips-loading">
              <Spinner className="size-5 text-foreground-subtle" />
            </div>
          ) : (
            skips.map((skip) => (
              <div
                key={skip.conversation_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5"
                data-testid="action-skip-row"
              >
                <div className="flex min-w-0 items-center gap-2 text-xs">
                  <span className="shrink-0 text-foreground-subtle">
                    {formatRelativeTime(skip.last_skipped_at)}
                  </span>
                  {skip.missing_values.length > 0 && (
                    <span className="truncate font-mono text-muted-foreground">
                      {formatMissingValues(skip.missing_values)}
                    </span>
                  )}
                </div>
                <a
                  href={`/i/${SCENARIO_ID}/conversation/${skip.conversation_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  data-testid="action-skip-conversation-link"
                >
                  <ExternalLink className="size-3.5" />
                  {t('logs.view_conversation')}
                </a>
              </div>
            ))
          )}
          {hasNextPage && (
            <Button
              size="xs"
              variant="ghost"
              className="text-foreground-subtle"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
              data-testid="action-skips-show-more"
            >
              {t('skips.show_more')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
